/** Token page reads (lane P1): price candles and holder earnings. */
import { queryOptions, useQuery } from '@tanstack/react-query'
import type { CandleInterval } from '@algoboo/schema'
import { request } from '../client'
import { isApiError } from '../errors'

const ADDR = /^0x[0-9a-fA-F]{40}$/

export const tokenKeys = {
  candles: (address: string, interval: CandleInterval, limit: number) => ['tokens', 'candles', address.toLowerCase(), interval, limit] as const,
  earnings: (address: string) => ['tokens', 'earnings', address.toLowerCase()] as const,
}

/** Market data that is not configured or unreachable answers 503 `DECODED_LAYER_UNCONFIGURED`: no retry storm. */
export function isPriceDataUnavailable(err: unknown): boolean {
  return isApiError(err) && (err.code === 'DECODED_LAYER_UNCONFIGURED' || err.status === 503 || err.code === 'NOT_IMPLEMENTED')
}

/** Whether the token reads may ask for this address at all. */
export const isTokenAddress = (address: string | null | undefined): boolean => ADDR.test(address ?? '')

export function tokenCandlesQuery(address: string, interval: CandleInterval, limit = 120) {
  const a = address.toLowerCase() as `0x${string}`
  return queryOptions({
    queryKey: tokenKeys.candles(a, interval, limit),
    queryFn: ({ signal }) => request('getTokenCandles', { signal, params: { address: a }, query: { interval, limit } }),
    staleTime: 30_000,
    refetchInterval: interval === '5m' || interval === '15m' ? 60_000 : 5 * 60_000,
    retry: (count, err) => !isPriceDataUnavailable(err) && !(isApiError(err) && err.isClientError) && count < 1,
  })
}

/** `GET /tokens/:address/candles`, oldest first. */
export function useTokenCandles(address: string | null | undefined, interval: CandleInterval, limit = 120, enabled = true) {
  return useQuery({ ...tokenCandlesQuery(address ?? '', interval, limit), enabled: enabled && isTokenAddress(address) })
}

export function tokenEarningsQuery(address: string) {
  const a = address.toLowerCase() as `0x${string}`
  return queryOptions({
    queryKey: tokenKeys.earnings(a),
    queryFn: ({ signal }) => request('getTokenEarnings', { signal, params: { address: a }, query: { limit: 26 } }),
    staleTime: 30_000,
    refetchInterval: 60_000,
  })
}

/** `GET /tokens/:address/earnings`: collected, allocated, funded, paid per epoch and the open epoch. */
export function useTokenEarnings(address: string | null | undefined, enabled = true) {
  return useQuery({ ...tokenEarningsQuery(address ?? ''), enabled: enabled && isTokenAddress(address) })
}
